import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

// @mui
import { Box, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import { SimBContainer } from '../container';

import { getAssetsUrl } from '../../utils/@helper/file.helper';

// ================================|| MAIN PAGE CONTAINER ||=============================== //
const ZonePageContainer = styled(Box)(
  ({ theme }) => `
    padding-bottom: ${theme.spacing(10)};
`
);
const SimZonePageContainer = ({ children, header }) => {
  const theme = useTheme();
  return (
    <ZonePageContainer className="Sim-zone-page-container">
      {header}
      <SimBContainer sx={{
        [theme.breakpoints.up('lg')]: {
          marginTop: theme.spacing(-20.75),
        },
        [theme.breakpoints.down('lg')]: {
          px: theme.spacing(3),
          marginTop: theme.spacing(-8),
        }
      }}>
        {children}
      </SimBContainer>
    </ZonePageContainer>
  );
};
SimZonePageContainer.propTypes = {
  children: PropTypes.node.isRequired,
  header: PropTypes.node,
};

// =================================|| SUB PAGE HEADER ||================================= //
const ZonePageHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgImage',
})(({ bgImage, theme }) => ({
  position: 'relative',
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  backgroundOrigin: 'border-box',
  backgroundColor: theme.palette.background.default,
  backgroundImage: `url('${bgImage}')`,
  // paddingTop: theme.spacing(23),
  // paddingBottom: theme.spacing(29),
  minHeight: 480,
  [theme.breakpoints.down('lg')]: {
    minHeight: 320,
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: 264,
  },
  '&:after': {
    content: '""',
    width: '100%',
    height: '100%',
    position: 'absolute',
    left: '50%',
    bottom: 0,
    transform: 'translate(-50% , 0)',
    zIndex: 0,
    backgroundImage: theme.palette.gradients.banner2,
  }
}));
const SimZonePageHeader = ({ bgImage, title, titleAlign, titleUnderline, subtitle }) => {
  const theme = useTheme();
  return (
    <ZonePageHeader className="Sim-main-page-header" bgImage={bgImage}>
      <SimBContainer
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'center', md: `center`, lg: titleAlign }
        }}
      >
        <Box>
          {
            title && (
              <Typography
                variant={'h2'}
                fontWeight={700}
                textAlign={'center'}
                color={'common.white'}
                zIndex={0}
                mb={6}
                sx={{
                  [theme.breakpoints.up('lg')]: {
                    ...(titleUnderline ? theme.typography.leftLine : {})
                  },
                  [theme.breakpoints.down('lg')]: {
                    ...(titleUnderline ? theme.typography.underLine : {})
                  }
                }}
              >
                { title }
              </Typography>
            )
          }
        </Box>
        {
          subtitle && (
            <Box>
              {subtitle}
            </Box>
          )
        }
      </SimBContainer>
    </ZonePageHeader>
  );
};
SimZonePageHeader.propTypes = {
  title: PropTypes.string.isRequired
};


// ==================================|| MAIN PAGE BODY ||================================== //
const ZonePageBody = styled(Box)(
  ({ theme }) => `
  position: relative;
  background-color: ${theme.palette.common.white};
`
);
const SimZonePageBody = ({ children }) => {
  const theme = useTheme();
  return (
    <ZonePageBody className="Sim-zone-page-body">
      {children}
    </ZonePageBody>
  );
};
SimZonePageBody.propTypes = {
  children: PropTypes.node.isRequired
};


// ==============================|| MAIN PAGE ||============================== //
const ZonePage = forwardRef((
  {
    title = '',
    titleAlign = 'flex-end',
    titleUnderline = true,
    subtitle = '',
    breadcrumbs = [],
    bgImage = getAssetsUrl('images/banners/banner-base.png'),
    //
    children,
    footer,
    disableGutters = true,
    sx,
    ...other
  },
  ref
) => {
  const theme = useTheme()
  const { t } = useTranslation();

  const padding = disableGutters ? {
    padding: 0
  } : {};


  return (
    <>
      <Helmet>
        <title>{`${title || ''}`}</title>
      </Helmet>

      <SimZonePageContainer
        header={<SimZonePageHeader bgImage={bgImage} title={title} titleAlign={titleAlign} titleUnderline={titleUnderline} subtitle={subtitle} />}
      >
        <SimZonePageBody>
          {children}
        </SimZonePageBody>

        {
          footer && (
            <Box>
              { footer }
            </Box>
          )
        }
      </SimZonePageContainer>
    </>
  )
});

ZonePage.propTypes = {
    children: PropTypes.node.isRequired,
    title: PropTypes.string,
    meta: PropTypes.node,
};

export default ZonePage;
